import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PaginationQueryInterface } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { RoomFindInterface } from '@zaoblako/shared/modules/catalog/dtos/room/room-find.dto';
import { TransitTaskFindInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-find.dto';
import { TransitTaskPartialFormInterface } from '@zaoblako/shared/modules/cctv/dtos/transit-task/transit-task-partial-form.dto';
import { TransitTaskStatusEnum } from '@zaoblako/shared/modules/cctv/enums/transit-task/transit-task-status.enum';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { TransitTaskService } from '@zaoblako/web/core/services/passage/transit-task.service';
import { UserGroupService } from '@zaoblako/web/core/services/security/user-group.service';
import { StoreService } from '@zaoblako/web/core/services/store.service';
import { TitleService } from '@zaoblako/web/core/services/title.service';
import { KeysOfType } from '@zaoblako/web/core/types/keys-of.type';
import { TransitTaskState } from '@zaoblako/web/modules/passage/components/transit-task/card/models/transit-task-state';
import { transitTaskInitialState } from '@zaoblako/web/modules/passage/components/transit-task/card/state/transit-task-initial-state';
import { BehaviorSubject, forkJoin } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TransitTaskStoreService extends StoreService<TransitTaskState> {
  protected state = new BehaviorSubject(transitTaskInitialState);

  public get getState() {
    return this.state.value;
  }

  public constructor(
    private readonly transitTaskService: TransitTaskService,
    private readonly clientService: ClientService,
    private readonly userGroupService: UserGroupService,
    private readonly titleService: TitleService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly router: Router
  ) {
    super();
  }

  public initState(taskId: number) {
    this.loadTask(taskId);
    this.loadEntities();
    this.loadPanelsInfo();
  }

  public loadTask(taskId: number) {
    this.updateState({ loading: true });
    this.transitTaskService.findById<TransitTaskFindInterface>(taskId).subscribe({
      next: (task) => {
        this.titleService.setTitle(`Заявка №${task?.id}`);
        this.updateState({ task, canEdit: task.status === TransitTaskStatusEnum.NEW, loading: false });
      }
    });
  }

  public deleteTask() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${this.getState.task?.id}?`,
      accept: () => {
        this.updateState({
          loading: true
        });

        this.transitTaskService.deleteById(this.getState.task?.id).subscribe(() => {
          this.errorService.addSuccessMessage(`Заявка №${this.getState.task?.id} удалена`);
          this.router.navigate(['/passage/transit-task']).catch(console.error);
        });
      }
    });
  }

  public updateFieldTask(key: keyof TransitTaskPartialFormInterface, value: any) {
    this.transitTaskService
      .patch<any, TransitTaskFindInterface>({ id: this.getState.task?.id, [key]: value || null })
      .subscribe({
        next: (task) => {
          this.updateState({ task });
        }
      });
  }

  public updateFieldTaskPeriod(from: Date, to: Date) {
    this.transitTaskService.patch({ id: this.getState.task?.id, from: from || null, to: to || null }).subscribe();
    const task: TransitTaskFindInterface = { ...this.getState.task, from: from || null, to: to || null };
    this.updateState({ task });
  }

  public loadPanelsInfo() {
    if (localStorage.getItem('transitTask.panelsInfo')) {
      const panelsInfo = JSON.parse(localStorage.getItem('transitTask.panelsInfo'));
      Object.keys(this.getState.panelsInfo).forEach((key) => {
        panelsInfo[key] = panelsInfo.hasOwnProperty(key) ? panelsInfo[key] : this.getState.panelsInfo[key];
      });
      this.updateState({ panelsInfo });
    }
  }

  public updatePanelsInfo(key: KeysOfType<typeof this.getState.panelsInfo, any>, value: any) {
    const info = this.getState.panelsInfo;
    info[key] = value;
    this.updateState({ panelsInfo: info });
    localStorage.setItem('transitTask.panelsInfo', JSON.stringify(info));
  }

  public passCar(room: RoomFindInterface) {
    if (!room) {
      return;
    }

    this.updateState({ loadingChangeStatus: true });

    this.transitTaskService.passCar(this.getState.task?.id, room.id).subscribe({
      next: (task) => {
        this.errorService.addSuccessMessage('Статус успешно изменен');
        this.updateState({ task, loadingChangeStatus: false });
      },
      error: () => {
        this.updateState({ loadingChangeStatus: false });
      }
    });
  }

  public loadHistory(queryParams: PaginationQueryInterface) {
    let state = { ...this.state.value };
    this.updateState({
      historyInfo: {
        ...state.historyInfo,
        queryParams,
        loading: true
      }
    });

    this.transitTaskService.getHistory(this.getState.task?.id, this.getState.historyInfo?.queryParams).subscribe({
      next: (history) => {
        state = { ...this.state.value };
        this.updateState({
          historyInfo: {
            ...state.historyInfo,
            count: history.count,
            items: history.items,
            loading: false
          }
        });
      }
    });
  }

  public onScrollEndHistory() {
    let historyInfo = this.getState.historyInfo;
    if (historyInfo?.items?.length < historyInfo?.count && !historyInfo?.paginationLoading && !historyInfo?.loading) {
      this.updateState({
        historyInfo: {
          ...historyInfo,
          queryParams: {
            ...historyInfo.queryParams,
            offset: historyInfo.queryParams.offset + historyInfo.queryParams.limit
          },
          paginationLoading: true
        }
      });

      this.transitTaskService.getHistory(this.getState.task?.id, this.getState.historyInfo?.queryParams).subscribe({
        next: (history) => {
          historyInfo = this.getState.historyInfo;
          this.updateState({
            historyInfo: {
              ...historyInfo,
              count: history.count,
              items: historyInfo.items.concat(history.items),
              paginationLoading: false
            }
          });
        }
      });
    }
  }

  public loadFiles() {
    this.updateState((state) => ({
      filesInfo: {
        loading: true,
        files: state.filesInfo.files
      }
    }));

    this.transitTaskService.getFiles(this.getState.task?.id).subscribe({
      next: (files) => {
        this.updateState({
          filesInfo: {
            files: files,
            loading: false
          }
        });
      }
    });
  }

  public updateFilesCount(count: number) {
    this.updateState((state) => ({
      task: {
        ...state.task,
        filesCount: state.task.filesCount + count
      }
    }));
  }

  private loadEntities() {
    forkJoin([
      this.userGroupService.find<UserGroupFindInterface>(),
      this.clientService.findAll({ select: ['id', 'name', 'contragents', 'externalUsers'] })
    ]).subscribe(([userGroups, clients]) => {
      this.updateState({
        userGroups,
        clients
      });
    });
  }
}
